// src/components/sections/ExpertiseSection.jsx
import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import expertise from "../../assets/images/expertise.jpg";

const ExpertiseSection = () => {
  const { isDark } = useTheme();
  
  const qualities = [
    {
      title: 'Functionality',
      description: 'Software products designed to perform specific functions and business tasks'
    },
    {
      title: 'Customization',
      description: 'Personalize and adapt systems according to your operational requirements'
    },
    {
      title: 'Compatibility',
      description: 'Solutions accessible across multiple platforms and systems'
    },
    {
      title: 'Security',
      description: 'Incorporating encryption and user authentication measures'
    }
  ];
  
  return (
    <section className={`relative py-24 sm:py-32 overflow-hidden ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* 2 Column Overlapping Layout - Image Card & Text Card */}
        <div className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-12 mb-24">
          
          {/* Left Column - Image Card */}
          <div className="relative w-full lg:w-1/2">
            <div className={`rounded-3xl overflow-hidden border shadow-2xl min-h-[500px] relative ${
              isDark ? 'border-white/20' : 'border-gray-200'
            }`}>
              
              {/* Decorative elements - only in dark mode */}
              {isDark && (
                <>
                  <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-700/20 rounded-full blur-3xl z-0" />
                  <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-blue-700/20 rounded-full blur-3xl z-0" />
                </>
              )}
              
              {/* Image */}
              <img 
                src={expertise}
                alt="Software development team collaborating on business solutions"
                className="w-full h-full object-cover min-h-[500px] scale-105 hover:scale-100 transition-transform duration-700"
              />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              
              {/* Caption overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className={`backdrop-blur-md rounded-xl px-4 py-3 border ${
                  isDark 
                    ? 'bg-black/60 border-white/10' 
                    : 'bg-white/80 border-gray-200'
                }`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Cross-industry expertise
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Construction • Manufacturing • Mining • Oil & Gas • Food
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Text Card (Overlaps the image) */}
          <div className="relative z-20 w-full lg:w-1/2 lg:-ml-20 mt-8 lg:mt-40">
            <div className={`backdrop-blur-xl rounded-3xl border p-8 md:p-10 shadow-2xl ${
              isDark 
                ? 'bg-gradient-to-br from-gray-900/95 to-black/95 border-white/20'
                : 'bg-white/95 border-gray-200 shadow-lg'
            }`}>
              
              {/* Badge */}
              <div className="mb-6">
                <span className={`inline-block px-4 py-2 rounded-full text-xs tracking-[0.2em] font-medium ${
                  isDark
                    ? 'bg-blue-700/10 border border-blue-700/30 text-blue-700'
                    : 'bg-blue-100 border border-blue-200 text-blue-700'
                }`}>
                  A PROVEN EXPERTISE
                </span>
              </div>
              
              {/* Heading */}
              <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Reshaping the landscape of
                <br />
                <span className="bg-gradient-to-r from-blue-700 via-blue-700 to-blue-700 bg-clip-text text-transparent">
                  business solutions
                </span>
              </h2>
              
              {/* Description */}
              <p className={`text-base sm:text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Delivering SaaS products and software solutions designed to improve 
                operational efficiency and business growth
              </p>
              
              {/* Who We Are */}
              <div className={`pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="mb-3">
                  <span className={`text-xs tracking-[0.2em] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    WHO WE ARE
                  </span>
                </div>
                <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Where innovation meets functionality;  Founded with the goal of transforming 
                  software development and SaaS applications by providing industry-leading 
                  solutions tailored to client-specific business needs
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Core Software Characteristics - Horizontal Flex Layout */}
        <div>
          <div className="text-center mb-12">
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                CORE SOFTWARE CHARACTERISTICS
              </span>
            </div>
            <h3 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Built on four key pillars
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualities.map((quality, index) => (
              <div 
                key={index} 
                className={`backdrop-blur-sm rounded-2xl p-6 border transition-all duration-300 group ${
                  isDark
                    ? 'bg-white/5 border-white/10 hover:border-blue-700/30 hover:bg-white/10'
                    : 'bg-gray-100 border-gray-200 hover:border-blue-700 hover:bg-gray-50'
                }`}
              >
                <h4 className={`text-xl font-semibold mb-3 transition-colors ${isDark ? 'text-white group-hover:text-blue-700' : 'text-gray-900 group-hover:text-blue-700'}`}>
                  {quality.title}
                </h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {quality.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Bottom decorative gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-700/30 to-transparent" />
    </section>
  );
};

export default ExpertiseSection;